import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, GitBranch, ArrowLeft, Trash2, ChevronDown, Settings2, ExternalLink, Plus, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useGitSettings } from '@/hooks/useGitSettings';
import { useGitHubOAuth } from '@/hooks/useGitHubOAuth';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useNavigate } from 'react-router-dom';
import type { GitCredential } from '@/contexts/GitSettingsContext';
import { PasswordInput } from '@/components/ui/password-input';
import { ExternalFavicon } from '@/components/ExternalFavicon';

interface PresetProvider {
  id: string;
  name: string;
  origin: string;
  username: string;
  tokenURL?: string;
}

const PRESET_PROVIDERS: PresetProvider[] = [
  {
    id: "github",
    name: "GitHub",
    origin: "https://github.com",
    username: "git",
    tokenURL: "https://github.com/settings/tokens",
  },
  {
    id: "gitlab",
    name: "GitLab",
    origin: "https://gitlab.com",
    username: "git",
    tokenURL: "https://gitlab.com/-/user_settings/personal_access_tokens",
  },
];

export function GitSettings() {
  const { t } = useTranslation();
  const { settings, addCredential, removeCredential, updateCredential, updateSettings } = useGitSettings();
  const { initiateOAuth, isLoading: isOAuthLoading, error: oauthError, isOAuthConfigured } = useGitHubOAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [customOrigin, setCustomOrigin] = useState('');
  const [customUsername, setCustomUsername] = useState('');
  const [customPassword, setCustomPassword] = useState('');
  const [presetTokens, setPresetTokens] = useState<Record<string, string>>({});
  const [forceManualEntry, setForceManualEntry] = useState<Record<string, boolean>>({});
  const [githubClientId, setGithubClientId] = useState(settings.githubOAuth?.clientId || '');
  const [githubClientSecret, setGithubClientSecret] = useState(settings.githubOAuth?.clientSecret || '');

  // Sync local state with settings when they change
  useEffect(() => {
    setGithubClientId(settings.githubOAuth?.clientId || '');
    setGithubClientSecret(settings.githubOAuth?.clientSecret || '');
  }, [settings.githubOAuth?.clientId, settings.githubOAuth?.clientSecret]);

  const handleSaveGitHubOAuth = () => {
    if (githubClientId.trim() && githubClientSecret.trim()) {
      updateSettings({
        githubOAuth: {
          clientId: githubClientId.trim(),
          clientSecret: githubClientSecret.trim(),
        }
      });
    }
  };

  const handleRemoveGitHubOAuth = () => {
    updateSettings({ githubOAuth: undefined });
    setGithubClientId('');
    setGithubClientSecret('');
  };

  const hasGitHubOAuthConfig = !!(settings.githubOAuth?.clientId && settings.githubOAuth?.clientSecret);
  const hasEnvOAuthConfig = !!(import.meta.env.VITE_GITHUB_OAUTH_CLIENT_ID && import.meta.env.VITE_GITHUB_OAUTH_CLIENT_SECRET);

  const handleAddPresetProvider = (preset: PresetProvider) => {
    const token = presetTokens[preset.id];
    if (!token?.trim()) return;

    addCredential(preset.origin, { username: preset.username, password: token.trim() });
    setPresetTokens(prev => ({ ...prev, [preset.id]: '' }));
  };

  const handleAddCustomProvider = () => {
    if (!customOrigin.trim() || !customUsername.trim() || !customPassword.trim()) return;
    addCredential(customOrigin.trim(), { username: customUsername.trim(), password: customPassword.trim() });
    setCustomOrigin('');
    setCustomUsername('');
    setCustomPassword('');
  };

  const configuredOrigins = Object.keys(settings.credentials);
  const availablePresets = PRESET_PROVIDERS.filter(preset => !configuredOrigins.includes(preset.origin));

  return (
    <div className="p-6 space-y-8">
      {isMobile && (
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/settings')}
            className="h-8 px-2 -ml-2 text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('settings')}
          </Button>
        </div>
      )}

      <div>
        <h1 className="text-xl font-semibold tracking-tight">{t('gitSettings')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('gitSettingsDescriptionLong')}
        </p>
      </div>

      <div className="space-y-6">
        {/* Configured Credentials */}
        {configuredOrigins.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t('configuredCredentials')}</h3>
            <Accordion type="multiple" className="space-y-2">
              {configuredOrigins.map((origin) => {
                const preset = PRESET_PROVIDERS.find(p => p.origin === origin);
                const credential = settings.credentials[origin];
                const isCustom = !preset;

                return (
                  <AccordionItem key={origin} value={origin} className="border rounded-lg bg-card">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline">
                      <div className="flex items-center gap-3">
                        <ExternalFavicon url={origin} size={16} fallback={<GitBranch size={16} className="text-muted-foreground" />} />
                        <span className="text-sm font-medium">{preset?.name || new URL(origin).hostname}</span>
                        {isCustom && <Badge variant="outline" className="text-xs">{t('custom')}</Badge>}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-sm">{t('origin')}</Label>
                          <Input value={origin} disabled className="h-9 bg-muted" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">{t('username')}</Label>
                          <Input
                            value={credential?.username || ''}
                            onChange={(e) => updateCredential(origin, { username: e.target.value })}
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">{t('password')}</Label>
                          <PasswordInput
                            value={credential?.password || ''}
                            onChange={(e) => updateCredential(origin, { password: e.target.value })}
                            className="h-9"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCredential(origin)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('delete')}
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}

        {/* Available Preset Providers */}
        {availablePresets.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t('addProvider')}</h3>
            <div className="grid gap-3">
              {availablePresets.map((preset) => (
                <div key={preset.id} className="border rounded-lg p-4 bg-card space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ExternalFavicon url={preset.origin} size={16} fallback={<GitBranch size={16} className="text-muted-foreground" />} />
                      <span className="text-sm font-medium">{preset.name}</span>
                    </div>
                    {preset.tokenURL && (preset.id !== 'github' || !isOAuthConfigured) && (
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-foreground underline"
                        onClick={() => window.open(preset.tokenURL, '_blank')}
                      >
                        {t('getToken')}
                      </button>
                    )}
                  </div>

                  {preset.id === 'github' && isOAuthConfigured && !forceManualEntry[preset.id] ? (
                    <div className="space-y-2">
                      <div className="flex gap-0">
                        <Button
                          onClick={initiateOAuth}
                          disabled={isOAuthLoading}
                          className="flex-1 rounded-r-none gap-2 h-9"
                        >
                          {isOAuthLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                              Connecting...
                            </>
                          ) : (
                            <>
                              <ExternalFavicon url="https://github.com" size={16} fallback={<GitBranch size={16} />} />
                              Connect to GitHub
                            </>
                          )}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="default" className="rounded-l-none border-l border-primary-foreground/20 px-2 h-9" disabled={isOAuthLoading}>
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setForceManualEntry(prev => ({ ...prev, [preset.id]: true }))}>
                              Enter API key
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {oauthError && <p className="text-sm text-destructive">{oauthError}</p>}
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <PasswordInput
                        placeholder={t('enterToken')}
                        className="flex-1 h-9"
                        value={presetTokens[preset.id] || ''}
                        onChange={(e) => setPresetTokens(prev => ({ ...prev, [preset.id]: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && presetTokens[preset.id]?.trim() && handleAddPresetProvider(preset)}
                      />
                      <Button onClick={() => handleAddPresetProvider(preset)} disabled={!presetTokens[preset.id]?.trim()} size="sm" className="h-9">
                        {t('add')}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Custom Provider */}
        <Accordion type="single" collapsible>
          <AccordionItem value="custom-provider" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-3">
                <Plus className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('addCustomProvider')}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm">{t('origin')}</Label>
                  <Input placeholder="https://git.example.com" value={customOrigin} onChange={(e) => setCustomOrigin(e.target.value)} className="h-9" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">{t('username')}</Label>
                  <Input placeholder="git" value={customUsername} onChange={(e) => setCustomUsername(e.target.value)} className="h-9" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">{t('password')}</Label>
                  <PasswordInput placeholder={t('enterPassword')} value={customPassword} onChange={(e) => setCustomPassword(e.target.value)} className="h-9" />
                </div>
                <Button
                  onClick={handleAddCustomProvider}
                  disabled={!customOrigin.trim() || !customUsername.trim() || !customPassword.trim() || customOrigin.trim() in settings.credentials}
                  size="sm"
                  className="gap-2"
                >
                  <Check className="h-4 w-4" />
                  {t('addCustomProviderButton')}
                </Button>
                {customOrigin.trim() in settings.credentials && (
                  <p className="text-sm text-destructive">{t('credentialsExist')}</p>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* GitHub OAuth Configuration */}
        {!hasEnvOAuthConfig && (
          <Accordion type="single" collapsible>
            <AccordionItem value="github-oauth" className="border rounded-lg bg-card">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{t('gitHubOAuthConfig')}</span>
                  {hasGitHubOAuthConfig && <Badge variant="outline" className="text-xs">{t('configured')}</Badge>}
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {t('gitHubOAuthDescription')}
                  </p>
                  <div className="flex items-center gap-2 text-sm">
                    <a
                      href="https://github.com/settings/developers"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      {t('createGitHubOAuthApp')}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  <div className="rounded-md bg-muted p-3 text-xs space-y-1">
                    <p><strong>{t('callbackUrl')}:</strong></p>
                    <code className="text-muted-foreground break-all">{window.location.origin}/oauth/github</code>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">{t('clientId')}</Label>
                    <Input
                      placeholder="Ov23li..."
                      value={githubClientId}
                      onChange={(e) => setGithubClientId(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">{t('clientSecret')}</Label>
                    <PasswordInput
                      placeholder="Enter client secret"
                      value={githubClientSecret}
                      onChange={(e) => setGithubClientSecret(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveGitHubOAuth}
                      disabled={!githubClientId.trim() || !githubClientSecret.trim()}
                      size="sm"
                      className="gap-2"
                    >
                      <Check className="h-4 w-4" />
                      {t('save')}
                    </Button>
                    {hasGitHubOAuthConfig && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveGitHubOAuth}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('delete')}
                      </Button>
                    )}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {/* Git Identity */}
        <Accordion type="single" collapsible>
          <AccordionItem value="git-identity" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('gitIdentity')}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm">{t('name')}</Label>
                  <Input placeholder="Your Name" value={settings.name || ''} onChange={(e) => updateSettings({ name: e.target.value })} className="h-9" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">{t('email')}</Label>
                  <Input type="email" placeholder="your.email@example.com" value={settings.email || ''} onChange={(e) => updateSettings({ email: e.target.value })} className="h-9" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm cursor-pointer">{t('coAuthoredByShakespeare')}</Label>
                  <Switch checked={settings.coAuthorEnabled ?? true} onCheckedChange={(checked) => updateSettings({ coAuthorEnabled: checked })} />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}

export default GitSettings;
